// rally 편집기 우열, 시퀀스 목록 하나
//
// **좌열의 결과를 읽기만 한다.** 적재할 때마다 즉시 쌓이고, 방금 넣은 줄이 점선으로
// 드러난다. 편집과 미리보기를 한 파일에 두면 폼을 고치는 스텝이 미리보기까지 함께
// 흔들게 되므로 발사대 스텝이 미리 갈라 두었다.
//
// ── 같은 시퀀스의 목록은 이것 하나다 (2026-08-27) ──────────────────────────
//
// 왜 합쳤는지는 `attack/index.tsx` 머리가 진다. 여기로 온 것은 옛 카드 목록이 들고 있던 둘,
// 정렬 토글(개시순, 착탄순)과 내보낸 뒤의 「고치기」 버튼이다. 이 목록이 조립 중, 마친 뒤,
// 내보낸 뒤 세 문맥을 전부 맡는다. 빼기 버튼은 내보내기 전에만 있고, 내보낸 뒤에는 DOM 에서
// 빠진다. 서버가 그 상태의 줄 빼기를 거절하므로 눌러도 되는 것처럼 생긴 거절되는 문을
// 남기지 않는다.
//
// ── 여기 뜨는 숫자에는 시각이 없다 ─────────────────────────────────────────
//
// 개시 Δ 는 앵커 상수의 차이이고 착탄 Δ 는 `offset` 그 자체다. 둘 다 시각과 무관해서,
// 이 목록은 **절대 시각을 안 그리는 것이 아니라 그릴 값을 갖고 있지 않다.** 계산은
// `use-sequence.ts` 의 `toRows` 가 하고 이 파일은 글자로 옮기기만 한다.
//
// ── 방금 넣은 줄을 왜 표시하는가 ───────────────────────────────────────────
//
// 목록이 정렬돼 있으므로 새 줄은 맨 아래가 아니라 착탄 순서나 개시 순서의 어딘가에
// 끼어든다. 줄이 다섯을 넘으면 그 자리가 눈에 안 들어오고, CT 는 자기가 방금 누른 것이
// 들어갔는지를 화면에 물어야 한다. 표시는 줄의 id 를 따라가므로 정렬을 바꿔도 그 줄에
// 붙어 있다. 명부가 같은 구멍을 열어 둔 채 남겨 둔 자리다(방금 넣은 줄이 닉네임 순
// 어딘가로 사라진다).
//
// ── 정렬 라벨은 이 파일이 갖지 않는다 ──────────────────────────────────────
//
// 판정 계약이 「구성 화면과 열람 화면의 토글 라벨이 같다」를 요구한다. 라벨을 두 벌로 쓰면
// 반드시 어긋나므로 문자열을 `common` 네임스페이스에 두고 열람 화면이 같은 키를 읽는다.
// **컴포넌트는 공유하지 않는다**, 두 화면에서 이 토글이 놓이는 자리도 크기도 다르다.
//
// ── main 의 삭제 마크는 비활성이다. 그러나 그것이 계약의 전부는 아니다 ─────
//
// 서버도 409 로 막는다. 화면에서 비활성으로 두는 것과 서버가 못 지우게 하는 것은 다르고,
// 판정 계약이 이 항목에 부분 점수를 두지 않았다. 화면만 막으면 요청을 직접 보내는 경로가
// 남고, 서버만 막으면 CT 는 눌러 본 뒤에야 안다.

import { Fragment } from 'react'

import type { Sequence } from '../../../../contract/schema.ts'
import type { RallyKind } from '../../../../core/timing/attack.ts'
import {
  type CommonStrings,
  common as commonStrings,
  type ComposeStrings,
  compose as composeStrings,
} from '../../../../i18n/index.ts'
import { useLang } from '../../../shell/use-lang.ts'
import { Figures } from '../../../ui/figures.tsx'
import { formatTimeText } from '../../../ui/time-input.tsx'
import { toRows } from '../attack/use-sequence.ts'
import { kindLabel, kindMark } from '../../../ui/rally-kind.ts'
import { KIND_BORDER } from './form-attack.tsx'
import { IconTile } from '../../../ui/icons.tsx'
import { LabelHint } from '../../../ui/label-hint.tsx'

/** 지금 고른 언어의 문자열 한 벌. **모듈에 굳히지 않는다**, 굳히면 언어를 바꿔도 안 따라온다. */
function useStrings(): ComposeStrings {
  return composeStrings(useLang().lang)
}

/** rally 타입 이름과 표식은 열람 카드도 같은 글자를 내야 해서 공용 네임스페이스가 진다. */
function useCommon(): CommonStrings {
  return commonStrings(useLang().lang)
}

/** 부호를 양수에도 붙인다. 생략하면 「안 넣었다」와 「0 보다 크게 넣었다」가 같은 글자다. */
export function signedDelta(millis: number): string {
  const body = formatTimeText(millis)
  return millis < 0 ? body : `+${body}`
}

/** 문장 안의 `{자리}` 를 채운다. 번역이 붙어 어순이 달라져도 견디게 하는 최소한이다. */
function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template,
  )
}

/** 목록의 정렬 축. 개시순과 착탄순 둘뿐이고 기본은 착탄순이다. */
export type SortAxis = 'open' | 'impact'

export interface RallyMiniSequenceProps {
  sequence: Sequence
  /** 맡은 사람의 이름. 계약의 rally 에는 이름이 없고 id 만 있다. */
  nameOf: (rosterId: string) => string
  /** 방금 적재된 줄. 없으면 `null`. */
  freshId: string | null
  /** 줄 빼기. 내보내기 전에만 버튼이 그려지므로 내보낸 뒤에는 불리지 않는다. */
  onRemove: (rallyId: string) => Promise<string | null>
  message: string
  /**
   * 정렬 상태는 부모가 든다. 목록이 스스로 들어도 되지만, 정렬 축을 바꾸는 순간 부모가
   * 알아야 할 일이 생기면(예: 줄 수정 패널이 가리키는 줄) 그때 다시 끌어올리게 된다.
   * 처음부터 부모에 두면 그 이사가 없다.
   */
  sortAxis: SortAxis
  onSort: (axis: SortAxis) => void
  /**
   * 내보낸 뒤 지금 고치는 중인 줄. **내보낸 뒤에만 뜻이 있다.** 그 전에는 고치기 버튼이
   * 없어서 값이 무엇이든 화면에 아무것도 안 그린다.
   */
  editingId: string | null
  /** 고치기 버튼. 같은 줄을 다시 누르면 부모가 영역을 닫는다(토글). 저장 안 한 입력이 있으면 부모가 먼저 묻는다. */
  onToggleEdit: (rallyId: string) => void
  /**
   * 고치는 줄의 편집 영역(`li` 하나). **그 행 바로 다음 자리에 그린다**(2026-10-14 사용자).
   * 목록은 그 영역의 속을 모른다. 시계와 서버 왕복을 쥔 부모가 만들어 넘긴다.
   */
  editor?: React.ReactNode
  /**
   * 고치기 버튼을 부모에게 알린다. 저장이나 취소 뒤 초점을 그 행의 버튼으로 돌려주려면 부모가
   * 버튼을 쥐어야 하는데, 행은 정렬과 방송으로 다시 그려지므로 id 로 찾는 ref 를 쓴다.
   */
  editButtonRef?: (rallyId: string, node: HTMLButtonElement | null) => void
}

export function RallyMiniSequence(props: RallyMiniSequenceProps): React.ReactElement {
  const t = useStrings()
  const c = useCommon()
  const rows = toRows(props.sequence.rallies)
  // 정렬은 파생값이다. 저장해 두면 방송으로 줄이 늘어난 순간 옛 순서가 남는다.
  const sorted = [...rows].sort((a, b) =>
    props.sortAxis === 'open'
      ? a.openDeltaMs - b.openDeltaMs
      : a.impactDeltaMs - b.impactDeltaMs,
  )
  const dispatched = props.sequence.state === 'dispatched'
  const subsRemain = props.sequence.rallies.some((line) => line.type !== 'main')
  // **main 이 없으면 잠긴 것도 없다.** main 담당자가 명부에서 내려가면 sub 만 남는데,
  // 그때도 잠금 문구를 그리면 화면이 「main 을 만듭니다」와 「main 을 뺄 수 없습니다」를
  // 두 줄 떨어진 자리에서 동시에 말한다. 그 문장이 가리키는 「main 의 −」는 화면에
  // 존재하지도 않는다. 파생시켰지만 **근거를 반쪽만 썼던 자리**다.
  // (2026-08-13 사람 관점 2회차 관측.)
  //
  // 내보낸 뒤에는 빼기 버튼 자체가 없으므로 잠금 문구도 없다. 없는 버튼을 설명하지 않는다.
  const mainLocked =
    !dispatched && subsRemain && props.sequence.rallies.some((line) => line.type === 'main')

  return (
    <section data-testid="rally-mini-sequence" aria-label={t.miniTitle} className="card min-w-0">
      <div className="panel-head cat-accent">
        <IconTile name="layers" tone="accent" />
        <h3 className="section-title">{t.miniTitle}</h3>
      </div>
      <p className="mt-line text-xs text-ink-mute">{t.miniWhy}</p>

      {rows.length === 0 ? (
        <p data-testid="mini-empty" className="mt-group text-sm text-ink-mute">
          {t.miniEmpty}
        </p>
      ) : (
        <>
          {/* ── 정렬 토글 ─────────────────────────────────────────────────
              라벨이 그 자체로 무엇을 보여 주는지 말한다. 「개시순」과 「착탄순」만 적으면
              둘 중 어느 것이 무엇인지가 게임을 아는 사람에게만 읽힌다. 줄이 없으면 토글도
              없다. 정렬할 것이 없는데 정렬 버튼이 서 있으면 눌러도 아무 일이 없는 조작이
              된다. */}
          <div className="mt-group flex flex-wrap items-center gap-2" data-testid="sort-toggle">
            <span className="text-sm text-ink-dim">{c.sortLabel}</span>
            {/* 장식 묶음(감싸는 span): 두 칸을 한 홈 안에 세운다. */}
            <span className="seg-group seg-group-fill">
            {/* 칸 안은 라벨(개시순) 위, 뜻풀이(먼저 여는 순서) 아래 두 요소다(2026-10-14 사용자 지시, 대시로 잇지 않는다). */}
            {(
              [
                ['open', c.sortByOpen, c.sortByOpenHint],
                ['impact', c.sortByImpact, c.sortByImpactHint],
              ] as const
            ).map(([axis, label, hint]) => (
              <button
                key={axis}
                type="button"
                data-testid={`sort-${axis}`}
                data-active={String(props.sortAxis === axis)}
                aria-pressed={props.sortAxis === axis}
                className="seg"
                onClick={() => {
                  props.onSort(axis)
                }}
              >
                <LabelHint label={label} hint={hint} stack hintClassName="text-xs font-normal text-ink-mute" />
              </button>
            ))}
            </span>
            <span className="ml-auto text-xs text-ink-mute">
              {fill(t.cardCount, { n: String(rows.length) })}
            </span>
          </div>

          {/* 두 Δ 가 무엇 대비인지를 머리에 적는다. 숫자만 있으면 같은 `+0:30.0` 이 개시
              대비인지 착탄 대비인지 화면에서 갈리지 않는다.

              **좁은 폭에서는 머리를 감추고 줄마다 라벨을 붙인다.** 줄이 접히면 칸이 세로로
              어긋나 머리가 가리키는 자리가 사라지는데, 그때 머리를 남겨 두면 그 글자가
              엉뚱한 숫자를 가리킨다.

              **분기점이 `md` 가 아니라 `lg` 인 이유**는 이 목록이 화면 전폭에 앉지 않기
              때문이다. 편집기가 768 에서 이미 2열로 갈라져 이 패널은 절반만 받는데, 그
              절반에 표의 고정 칸 넷(48, 80, 80, 32픽셀)과 사이 여백을 세우면 이름 칸에 남는
              것이 0픽셀이었다. 실제로 768 에서 이름이 폭 0 에 세 줄로 쪼개졌다.
              (2026-08-14 관측. `narrow-text.spec.ts` 가 그 자리를 기계로 잡는다.)

              마지막 빈 칸은 줄 오른쪽 조작의 폭과 맞춘다. 내보내기 전에는 빼기 마크(32픽셀),
              내보낸 뒤에는 고치기 버튼이라 더 넓다. */}
          <div className="table-head mt-group hidden gap-2 text-xs text-ink-dim lg:flex">
            <span className="flex-1">{t.columnRoster}</span>
            <span className="w-12 text-right">{t.columnRallying}</span>
            <span className="w-20 text-right">
              {t.columnOpenDelta}
              <br />
              <span className="text-ink-mute">{t.openDeltaWhy}</span>
            </span>
            <span className="w-20 text-right">
              {t.columnImpactDelta}
              <br />
              <span className="text-ink-mute">{t.impactDeltaWhy}</span>
            </span>
            <span className={dispatched ? 'w-16' : 'w-11'} />
          </div>

          <ul className="divide-y divide-line-hair" data-testid="mini-rows">
            {sorted.map((row) => {
              const kind = row.rally.type as RallyKind
              const fresh = props.freshId === row.rally.id
              // main 은 sub 가 남아 있는 동안 잠긴다. 잠금을 상태에서 파생시킨다.
              // 저장해 두면 sub 를 지운 뒤에도 잠금만 살아남는다.
              const locked = kind === 'main' && subsRemain
              // 줄 수정은 내보낸 뒤에만 있다. 조립이나 편집 문맥에서는 고치는 중이라는 표시를 그리지
              // 않는다. 다른 창이 「이 ops 수정」을 해 문맥이 바뀌는 순간 옛 표시가 남지 않게 한다.
              const editingThis = dispatched && props.editingId === row.rally.id
              // 고치는 행은 왼쪽 강조선을 긋고 아래 구분선을 지운다. 바로 다음 칸의 편집 영역이
              // 같은 선을 이어 그어 행과 영역이 한 묶음으로 읽힌다(2026-10-14 사용자, 상자는 더하지
              // 않는다). 방금 붙인 줄의 점선보다 이 표시가 앞선다. 둘이 겹치면 지금 손대는 줄이
              // 더 급한 정보다. 강조색은 왼쪽 변에만 준다.
              const rowMark = editingThis
                ? 'border-b-0 border-l-2 border-l-accent pl-2'
                : fresh
                  ? `border-l-2 border-dashed pl-2 ${KIND_BORDER[kind]}`
                  : ''
              return (
                <Fragment key={row.rally.id}>
                <li
                  data-testid="mini-row"
                  data-type={kind}
                  data-rally-id={row.rally.id}
                  data-fresh={String(fresh)}
                  data-editing={String(editingThis)}
                  // **좁은 폭에서는 줄이 격자다.** 첫 켜에 이름과 삭제 마크, 둘째 켜에 지표
                  // 2열. `flex-wrap` 으로 흘려 보냈더니 칸들이 남는 폭을 나눠 갖느라 라벨이
                  // 짓눌려 세로 막대가 됐다(2026-08-14 관찰, 340~767픽셀 전 구간). 격자는
                  // 켜를 미리 갈라 두므로 칸이 서로의 폭을 뺏지 않는다.
                  //
                  // 넓어지면 `lg:flex` 로 한 켜가 된다. 그때 아래의 자리 지정 class 들은
                  // 격자 전용 속성이라 flex 항목에서 저절로 무시된다, 되돌리는 class 를
                  // 따로 붙이지 않아도 되는 이유가 이것이다.
                  //
                  // 이름을 자기 켜에 통째로 두는 것은 앞서 고친 것을 그대로 지킨다. 이름 칸이
                  // 짓눌리면 「이 줄이 누구 것인가」가 사라지는데, 340, 360, 768 세 폭에서
                  // 실제로 이름이 통째로 비었다(2026-08-13 사람 관점 1회차 관측).
                  className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-line py-line text-sm lg:flex lg:px-2.5 ${rowMark}`}
                >
                  {/* 한 켜로 펴진 뒤 이름 칸은 `flex: 1 1 auto` 다. **`basis-auto` 를 빼면
                      안 된다**, 빼면 basis 가 0 이 되어 이 칸이 「내용만큼」이 아니라 「남는
                      폭의 몫만큼」만 받고, 남는 폭이 음수인 순간 0픽셀이 된다. 숫자 칸들은
                      자릿수 사이에 줄바꿈 자리가 없어 못 줄어들므로 부족분을 이름 칸이 혼자
                      뒤집어쓴다. */}
                  <span className="col-start-1 row-start-1 flex min-w-0 items-baseline gap-1.5 lg:flex-1 lg:basis-auto">
                    <span
                      className={`kind-tile kind-${kind} self-center font-mono`}
                      aria-label={kindLabel(kind, c)}
                    >
                      {kindMark(kind, c)}
                    </span>
                    {/* **이름은 줄이지 않는다.** 줄이면 `[RVN]…` 이 되어 누구인지 사라진다.
                        못 찾으면 식별자를 그대로 뱉지 않는 것은 `nameOf` 가 진다. */}
                    <span data-testid="mini-row-roster" className="min-w-0 font-medium">
                      {props.nameOf(row.rally.rosterId)}
                    </span>
                    {fresh && (
                      <span className="chip shrink-0">{t.miniFresh}</span>
                    )}
                  </span>
                  {/* 좁은 폭에서는 표 머리가 감춰지므로 숫자마다 라벨이 붙는다. 그 라벨과 값을
                      **한 칸에 묶는 일**을 공용 조각이 한다, 묶지 않으면 라벨만 다음 줄로
                      떨어져 나가 그 숫자의 이름이 사라진다. 넓은 폭의 칸 너비를 여기서 주는
                      이유는 위의 표 머리와 맞춰야 하기 때문이고, 그 짝은 바로 위 머리 줄의
                      `w-12`, `w-20`, `w-20` 이다. */}
                  <Figures
                    wide="table"
                    at="lg"
                    className="col-span-2 col-start-1 row-start-2"
                    figures={[
                      {
                        label: t.columnRallying,
                        value: formatTimeText(row.rally.rallyingMs),
                        testId: 'mini-row-rallying',
                        wide: 'lg:w-12',
                      },
                      {
                        label: t.columnOpenDelta,
                        value: signedDelta(row.openDeltaMs),
                        testId: 'mini-row-open-delta',
                        wide: 'lg:w-20',
                      },
                      {
                        label: t.columnImpactDelta,
                        value: signedDelta(row.impactDeltaMs),
                        testId: 'mini-row-impact-delta',
                        wide: 'lg:w-20',
                      },
                    ]}
                  />
                  {/* 줄 오른쪽 조작은 문맥이 정한다. 내보내기 전에는 빼기, 내보낸 뒤에는 고치기.
                      둘을 같이 그리지 않는다. 내보낸 뒤의 빼기는 서버가 거절하므로 버튼을
                      두면 눌러도 되는 것처럼 생긴 거절되는 문이 된다.

                      좁은 폭에서는 이름과 같은 켜의 오른쪽 끝에 앉는다. 지표 아래로 내려보내면
                      2열 격자의 셋째 칸을 차지해 숫자 하나와 나란히 서고, 그러면 그 숫자를
                      지우는 마크처럼 읽힌다. */}
                  {dispatched ? (
                    // **내보낸 뒤에도 고칠 수 있다.** 정본이 offset, 대기, 담당 roster 의
                    // 편집을 잠그지 않는다고 적었고, 잠그지 않는다는 것은 화면에 길이
                    // 보인다는 뜻이다, 길이 있는데 화면이 말하지 않으면 없는 것과 같다.
                    <button
                      type="button"
                      data-testid="mini-row-edit"
                      data-rally-id={row.rally.id}
                      aria-pressed={editingThis}
                      ref={(node) => {
                        props.editButtonRef?.(row.rally.id, node)
                      }}
                      // 버튼이 줄마다 같은 글자로 여럿 뜬다. 낭독기에는 그 줄이 누구의 줄인지가
                      // 안 실리므로 이름을 붙여 가른다. 빼기 버튼과 명부의 목록이 같은 규칙이다.
                      aria-label={`${props.nameOf(row.rally.rosterId)} ${t.editRally}`}
                      className="seg col-start-2 row-start-1 w-16 shrink-0 px-2"
                      onClick={() => {
                        props.onToggleEdit(row.rally.id)
                      }}
                    >
                      {t.editRally}
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="mini-row-remove"
                      // 버튼이 줄마다 같은 글자로 여럿 뜬다. 낭독기에는 그 줄이 누구의 줄인지가
                      // 안 실리므로 이름을 붙여 가른다. 명부의 목록이 같은 규칙을 쓴다.
                      aria-label={`${props.nameOf(row.rally.rosterId)} ${t.miniRemove}`}
                      disabled={locked}
                      title={locked ? t.miniMainLocked : undefined}
                      className={`btn btn-ghost col-start-2 row-start-1 w-11 shrink-0 px-0 text-base ${
                        locked ? '' : 'text-red-300'
                      }`}
                      onClick={() => void props.onRemove(row.rally.id)}
                    >
                      −
                    </button>
                  )}
                </li>
                {editingThis && props.editor}
                </Fragment>
              )
            })}
          </ul>

          {/* 잠긴 이유를 목록 밖에 한 번 적는다. 줄마다 적으면 같은 문장이 여럿 뜬다. */}
          {mainLocked && (
            <p data-testid="mini-main-locked" className="mt-line text-xs text-ink-mute">
              {t.miniMainLocked}
            </p>
          )}
        </>
      )}

      {props.message !== '' && (
        <p data-testid="mini-message" role="alert" className="mt-group text-sm text-red-300">
          {props.message}
        </p>
      )}
    </section>
  )
}
